// Dialog modificateurs d'une ligne (D6) : « SANS … » / « + … » -> `mod`
// (préfixes -/+) + texte libre optionnel. La composition finale en `notes_libres`
// est faite au moment du createCommande via composeNotes (aucune écriture ici).
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

export interface ModificateursValue {
  mod: string[];
  texte: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: ModificateursValue;
  onSave: (value: ModificateursValue) => void;
  titre: string;
  /** « Composition » pour la sur mesure (complément 7c) — champs/persistance
      inchangés, libellé d'affichage seul. */
  labelAjouts?: string;
}

function splitListe(csv: string): string[] {
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function ModificateursDialog({
  open,
  onOpenChange,
  value,
  onSave,
  titre,
  labelAjouts = 'Ajouts',
}: Props) {
  const [sans, setSans] = useState('');
  const [ajouts, setAjouts] = useState('');
  const [texte, setTexte] = useState('');

  // Ré-hydrate les champs à l'ouverture depuis le `mod` existant.
  useEffect(() => {
    if (!open) return;
    setSans(
      value.mod
        .filter((m) => m.startsWith('-'))
        .map((m) => m.slice(1))
        .join(', '),
    );
    setAjouts(
      value.mod
        .filter((m) => m.startsWith('+'))
        .map((m) => m.slice(1))
        .join(', '),
    );
    setTexte(value.texte);
  }, [open, value]);

  const enregistrer = (): void => {
    const mod = [
      ...splitListe(sans).map((s) => `-${s}`),
      ...splitListe(ajouts).map((s) => `+${s}`),
    ];
    onSave({ mod, texte: texte.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-cormorant text-2xl italic">
            {titre}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="font-jost text-sm">Sans (séparés par des virgules)</span>
            <Input
              value={sans}
              onChange={(e) => setSans(e.target.value)}
              placeholder="champignons, olives"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-jost text-sm">
              {labelAjouts} (séparés par des virgules)
            </span>
            <Input
              value={ajouts}
              onChange={(e) => setAjouts(e.target.value)}
              placeholder="parmesan"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-jost text-sm">Note libre</span>
            <Input
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="bien cuite"
            />
          </label>
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={enregistrer}
            className="min-h-[64px] min-w-[120px]"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
